import {
  useCallback, useEffect, useId, useLayoutEffect, useMemo, useRef, useState,
  type CSSProperties, type InputHTMLAttributes, type ReactNode, type RefObject,
  type TextareaHTMLAttributes,
} from 'react';
import { ErroApi, abreNumaAba, api, baixarBlob } from './api';
import { numero } from './formato';
import { tecladoDaTabela } from './tecladoDaTabela';

/**
 * As peças da tela.
 *
 * Cada uma existe para resolver um problema concreto do ambiente: o campo
 * amarra rótulo, erro e leitor de tela num nó só; a escolha troca lista
 * suspensa por botão porque no galpão são três toques contra um; o aviso dá
 * ao erro do servidor um lugar previsível para aparecer.
 */

// ── Campo de texto ───────────────────────────────────────────────────

interface CampoProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'id'> {
  rotulo: string;
  erro?: string;
  dica?: string;
}

export function Campo({ rotulo, erro, dica, ...resto }: CampoProps) {
  const id = useId();
  return (
    <label className="campo" htmlFor={id}>
      <span className="rotulo">{rotulo}</span>
      <input
        id={id}
        aria-invalid={erro ? true : undefined}
        aria-describedby={erro ? `${id}-e` : dica ? `${id}-d` : undefined}
        {...resto}
      />
      {dica && !erro && <span className="dica" id={`${id}-d`}>{dica}</span>}
      {erro && <span className="erro" id={`${id}-e`} role="alert">{erro}</span>}
    </label>
  );
}

interface AreaProps extends Omit<TextareaHTMLAttributes<HTMLTextAreaElement>, 'id'> {
  rotulo: string;
  erro?: string;
  dica?: string;
}

export function Area({ rotulo, erro, dica, ...resto }: AreaProps) {
  const id = useId();
  return (
    <label className="campo" htmlFor={id}>
      <span className="rotulo">{rotulo}</span>
      <textarea
        id={id}
        aria-invalid={erro ? true : undefined}
        aria-describedby={erro ? `${id}-e` : dica ? `${id}-d` : undefined}
        {...resto}
      />
      {dica && !erro && <span className="dica" id={`${id}-d`}>{dica}</span>}
      {erro && <span className="erro" id={`${id}-e`} role="alert">{erro}</span>}
    </label>
  );
}


// ── Campo numérico de bancada ────────────────────────────────────────

/**
 * O campo em que se digita massa, e por que ele não é `<input>` pelado.
 *
 * Três defeitos moravam no mesmo lugar, e todos os três vinham de um
 * `<input>` sem atributo nenhum dentro de célula de tabela:
 *
 *  1. **A regra de tamanho não pegava.** A folha casa altura de 48 px e
 *     fonte de 16 px por `input[type="…"]`, e input sem `type` não casa
 *     com nenhum deles: o campo de massa ficava com a altura natural do
 *     navegador, uns 30 px, num aparelho em que o dedo tem luva.
 *  2. **O Safari do iOS dava zoom** a cada toque, porque a fonte herdada
 *     era menor que 16 px. Depois do zoom a página fica maior que a
 *     janela e a pessoa passa a rolar de lado para achar o resto do
 *     formulário.
 *  3. **O teclado abria alfabético**, para digitar «22,30».
 *
 * `type="text"` — e não `type="number"` — é decisão, não descuido:
 * `number` recusa a vírgula decimal em teclado pt-BR, esconde o valor
 * quando ele não casa com o formato interno, e rola o número com a roda
 * do mouse por cima de um lançamento já digitado. O `inputMode` é o que
 * chama o teclado numérico; o `type` fica sendo o que casa com a folha.
 *
 * `autoComplete="off"` porque o navegador oferecendo «22,30» de outro
 * ensaio dentro de uma bateria de cápsulas é sugestão que vira erro de
 * pesagem.
 */
interface CampoNumericoProps
  extends Omit<InputHTMLAttributes<HTMLInputElement>,
    'id' | 'type' | 'inputMode' | 'autoComplete'> {
  /** O que a pessoa lê acima do campo. Em tabela, use `rotuloOculto`. */
  rotulo: string;
  /**
   * O rótulo existe para o leitor de tela, mas não aparece: numa tabela
   * quem nomeia a coluna é o `<th>`, e repetir o nome dentro de cada
   * célula é ruído para quem enxerga e falta para quem não enxerga.
   */
  rotuloOculto?: boolean;
  erro?: string;
  dica?: string;
  /** Golpes, número de determinações: teclado numérico sem vírgula. */
  inteiro?: boolean;
}

export function CampoNumerico({
  rotulo, rotuloOculto, erro, dica, inteiro, className, ...resto
}: CampoNumericoProps) {
  const id = useId();
  return (
    <label className={`campo campo-num ${className ?? ''}`.trim()} htmlFor={id}>
      <span className={rotuloOculto ? 'so-leitor' : 'rotulo'}>{rotulo}</span>
      <input
        id={id}
        type="text"
        inputMode={inteiro ? 'numeric' : 'decimal'}
        /* O teclado do celular mostra «seguinte» em vez de «ir»: numa
           bateria de cápsulas o gesto seguinte é sempre outro campo. */
        enterKeyHint="next"
        autoComplete="off"
        autoCorrect="off"
        spellCheck={false}
        aria-invalid={erro ? true : undefined}
        aria-describedby={erro ? `${id}-e` : dica ? `${id}-d` : undefined}
        {...resto}
      />
      {dica && !erro && <span className="dica" id={`${id}-d`}>{dica}</span>}
      {erro && <span className="erro" id={`${id}-e`} role="alert">{erro}</span>}
    </label>
  );
}

// ── A tabela de lançamento, que no celular vira pilha de cartões ─────

/**
 * Sete colunas não cabem em 390 px.
 *
 * Uma bateria de cápsulas é uma tabela de sete colunas — ordem, cápsula,
 * tara, bruto úmido, bruto seco, w, descarte. Num aparelho de bolso ela
 * só sobrevive de dois jeitos: rolando de lado, ou deixando de ser
 * tabela. A Testesolo escolheu o segundo (opção A · Bancada, 07/09/2026):
 * **abaixo de 640 px cada linha vira um cartão**, com o resultado no
 * cabeçalho do cartão e um campo por vez debaixo do polegar; **de 640 px
 * para cima continua tabela**, que é a folha que o laboratório já
 * preenche à mão e é nela que a pesagem trocada salta aos olhos.
 *
 * O corte é de VERDADE, e não `display: block` por CSS: mudar o
 * `display` de `<table>` apaga a semântica de tabela em vários leitores
 * de tela — a tabela deixa de ter linhas e as células param de ser
 * anunciadas com o cabeçalho da coluna. Aqui o celular recebe uma lista
 * de verdade, com `<dt>`/`<dd>`, e o desktop uma tabela de verdade.
 *
 * Nas duas, Enter e Tab pulam de célula e o Enter na última célula da
 * linha grava a linha (`tecladoDaTabela`, onda 354 · RN-275): o botão que
 * grava se marca `data-gravar-linha` (na linha) ou `data-gravar-tabela`
 * (logo depois da tabela, o que grava tudo de uma vez).
 */
export interface ColunaDeLancamento {
  texto: string;
  /** Alinhamento à direita e tabular — coluna de número. */
  num?: boolean;
}

export interface LinhaDeLancamento {
  chave: string;
  classe?: string;
  /** O cabeçalho do cartão: «Cápsula CAP-118», «Peneira 2,0 mm». */
  titulo: ReactNode;
  /** O número em destaque no cartão: w (%), passa (%). */
  resultado?: ReactNode;
  /**
   * Uma linha de contexto sob o cabeçalho do cartão, no celular.
   *
   * Serve ao valor que se LÊ e não se digita — a massa acumulada da
   * peneira, por exemplo. Como campo, ele custava um rótulo e uma linha
   * inteira em cada cartão; como nota, cabe ao lado do resultado e a
   * pilha de oito peneiras encolhe quase à metade. Na tabela ele não
   * aparece: lá a coluna já o mostra.
   */
  nota?: ReactNode;
  /** Uma célula por coluna, na ordem de `colunas`. */
  celulas: ReactNode[];
  /**
   * Colunas que o cartão NÃO repete porque já estão no título ou no
   * resultado. Índices de `colunas`.
   */
  semCartao?: number[];
}

export function TabelaDeLancamento({
  rotulo, colunas, linhas, classe,
}: {
  /** Nome da tabela para quem usa leitor de tela. Não aparece na tela. */
  rotulo: string;
  colunas: ColunaDeLancamento[];
  linhas: LinhaDeLancamento[];
  classe?: string;
}) {
  const estreito = useEstreito();

  if (estreito) {
    return (
      <ul className={`cartoes-lancamento ${classe ?? ''}`.trim()}
        aria-label={rotulo} {...tecladoDaTabela(rotulo)}>
        {linhas.map((l) => {
          const fora = new Set(l.semCartao ?? []);
          return (
            <li key={l.chave}
              className={`cartao-lancamento ${l.classe ?? ''}`.trim()}>
              <div className="cartao-topo">
                <b>{l.titulo}</b>
                {l.resultado != null && (
                  <span className="cartao-resultado">{l.resultado}</span>
                )}
              </div>
              {l.nota != null && <p className="cartao-nota">{l.nota}</p>}
              <dl className="cartao-campos">
                {colunas.map((c, i) => (fora.has(i) ? null : (
                  <div key={`${c.texto}-${i}`}
                    className={c.texto ? 'cartao-campo' : 'cartao-campo largo'}>
                    {c.texto && <dt>{c.texto}</dt>}
                    <dd>{l.celulas[i]}</dd>
                  </div>
                )))}
              </dl>
            </li>
          );
        })}
      </ul>
    );
  }

  return (
    <table className={`dados lancamento ${classe ?? ''}`.trim()}
      {...tecladoDaTabela(rotulo)}>
      <caption className="so-leitor">{rotulo}</caption>
      <thead>
        <tr>
          {colunas.map((c, i) => (
            <th key={`${c.texto}-${i}`} scope="col"
              className={c.num ? 'num' : undefined}>{c.texto}</th>
          ))}
        </tr>
      </thead>
      <tbody>
        {linhas.map((l) => (
          <tr key={l.chave} className={l.classe}>
            {l.celulas.map((cel, i) => (
              <td key={i} className={colunas[i]?.num ? 'num' : undefined}>
                {cel}
              </td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  );
}

/**
 * «Estou num aparelho estreito?», perguntado ao navegador.
 *
 * 640 px é o corte único do projeto para celular — o mesmo da folha. A
 * pergunta é feita com `matchMedia` e não com `window.innerWidth` porque
 * girar o aparelho tem que trocar o desenho, e `matchMedia` avisa.
 */
export function useEstreito(largura = 640): boolean {
  const [estreito, setEstreito] = useState(() => (
    typeof window !== 'undefined' && typeof window.matchMedia === 'function'
      ? window.matchMedia(`(max-width: ${largura}px)`).matches
      : false));

  useEffect(() => {
    if (typeof window === 'undefined'
      || typeof window.matchMedia !== 'function') return undefined;
    const mq = window.matchMedia(`(max-width: ${largura}px)`);
    const ouvir = () => setEstreito(mq.matches);
    ouvir();
    mq.addEventListener('change', ouvir);
    return () => mq.removeEventListener('change', ouvir);
  }, [largura]);

  return estreito;
}

// ── As abas que grudam (celular) ─────────────────────────────────────

/**
 * A aba tocada com as abas grudadas, à espera de aparecer. Mora no módulo, e
 * não num estado: a aba nova pode ser outra tela (a Programação e o
 * Cronograma não são a `OsTela`), e o pedido atravessa a troca. Vale por
 * alguns segundos, e morre com a tela que o pegou se ela sair antes de
 * chegar (`retomado`, Ruling R41): um pedido esquecido não rola a próxima
 * OS aberta.
 */
let abaPedida: { aba: string; ate: number; retomado?: boolean } | null = null;

/** O primeiro destes gestos é a pessoa assumindo a página: a rolagem desiste. */
const GESTOS_DA_PESSOA = ['wheel', 'touchstart', 'pointerdown', 'keydown'] as const;

/** Religa a âncora da rolagem desligada por `desligarAAncora`, se estiver. */
let religarAAncora: (() => void) | null = null;

/**
 * Desliga a âncora da rolagem do navegador (`overflow-anchor: none` no
 * `<html>`) por `ms`, ou até a pessoa rolar ou tocar — o que vier antes.
 * Um pedido novo recomeça a conta (religa o anterior e desliga de novo).
 */
function desligarAAncora(ms: number) {
  religarAAncora?.();
  const raiz = document.documentElement;
  raiz.style.overflowAnchor = 'none';
  const religar = () => {
    if (religarAAncora !== religar) return;
    religarAAncora = null;
    window.clearTimeout(relogio);
    for (const g of GESTOS_DA_PESSOA) window.removeEventListener(g, religar);
    raiz.style.removeProperty('overflow-anchor');
  };
  const relogio = window.setTimeout(religar, ms);
  for (const g of GESTOS_DA_PESSOA) window.addEventListener(g, religar, { passive: true });
  religarAAncora = religar;
}

/**
 * Onde a faixa das abas fica sem grudar (em px do topo do documento) e onde
 * ela gruda: o `top` da regra, `var(--altura-presa, 48px)`, já em px.
 */
function pontosDaFaixa(nav: HTMLElement) {
  const antes = nav.style.position;
  nav.style.position = 'static';
  const natural = nav.getBoundingClientRect().top + window.scrollY;
  nav.style.position = antes;
  const presa = parseFloat(getComputedStyle(nav).top);
  return { natural, presa: Number.isFinite(presa) ? presa : 48 };
}

/**
 * Trocar de aba com as abas grudadas (Rulings R33 e R38).
 *
 * No celular as abas da OS e do lote do recebimento grudam logo abaixo do
 * que está preso em cima (o topo, e a faixa da rede quando aparece). Quem
 * rolou até o fim das amostras e tocava em «Resumo» via a aba nova abrir
 * no meio, ou no fim: a rolagem ficava onde estava. Agora, se as abas
 * estavam grudadas no toque, a aba nova abre com elas no lugar onde grudam
 * e o começo do conteúdo logo abaixo — a página vai ao ponto natural das
 * abas menos a altura presa, num salto (`instant`), como abrir uma página.
 *
 * A aba nova pode chegar «Carregando» (Quantitativos, Programação,
 * Cronograma): a página ainda é curta e o salto para antes do alvo. Por
 * isso, a cada quadro em que a página cresce, a rolagem se repete — até
 * chegar ao alvo, até 2 s, ou até a pessoa rolar ou tocar, quando a página
 * passa a ser dela. Se a tela sai antes (o «voltar» do aparelho), o pedido
 * sai junto (Ruling R41).
 *
 * E o que chega DEPOIS de a página chegar ao alvo (a faixa do escopo no
 * Resumo do lote vem por outro pedido, uns 80 ms depois) entra acima do nó
 * que o navegador escolheu como âncora da rolagem (`overflow-anchor`) — com
 * o `scroll-padding-top` da 308, o primeiro bloco abaixo das abas —, e ele
 * descia a página o tanto que entrou: a aba nova abria 122 px abaixo das
 * abas (achado da integração das ondas 308 e 310). Por isso, enquanto a aba
 * nova carrega — os mesmos 2 s, ou até a pessoa rolar ou tocar —, a âncora
 * fica desligada (`desligarAAncora`): o que entra empurra o resto para
 * baixo, e o começo da aba fica logo abaixo das abas.
 *
 * Só no celular: no desktop as abas não grudam e nada muda. Devolve o
 * `onClick` de cada aba, que recebe a aba de destino.
 */
export function useAbasQueGrudam(faixa: RefObject<HTMLElement | null>, aba: string) {
  const estreito = useEstreito(640);
  useLayoutEffect(() => {
    const pedido = abaPedida;
    const nav = faixa.current;
    if (!estreito || !nav || !pedido || pedido.aba !== aba || Date.now() > pedido.ate) return undefined;
    pedido.retomado = true;
    desligarAAncora(2000);
    const raiz = document.documentElement;
    /* Vai ao alvo e diz se chegou (a página pode ainda ser curta). */
    const rolar = () => {
      const { natural, presa } = pontosDaFaixa(nav);
      const alvo = Math.max(0, natural - presa);
      if (Math.abs(window.scrollY - alvo) > 1) window.scrollTo({ top: alvo, behavior: 'instant' });
      return Math.abs(window.scrollY - alvo) <= 1;
    };
    let altura = raiz.scrollHeight;
    let quadro = 0;
    const fim = performance.now() + 2000;
    /* Parar de olhar. */
    function soltar() {
      cancelAnimationFrame(quadro);
      for (const g of GESTOS_DA_PESSOA) window.removeEventListener(g, desistir);
    }
    /* Chegou, acabou o prazo, a faixa saiu da tela, ou a pessoa assumiu: o
       pedido está cumprido. */
    function desistir() {
      if (abaPedida === pedido) abaPedida = null;
      soltar();
    }
    function passo() {
      if (performance.now() > fim || !nav!.isConnected) { desistir(); return; }
      if (raiz.scrollHeight !== altura) {
        altura = raiz.scrollHeight;
        if (rolar()) { desistir(); return; }
      }
      quadro = requestAnimationFrame(passo);
    }
    if (rolar()) { desistir(); return undefined; }
    for (const g of GESTOS_DA_PESSOA) window.addEventListener(g, desistir, { passive: true });
    quadro = requestAnimationFrame(passo);
    return () => {
      soltar();
      /* A tela saiu antes de chegar (o «voltar» do aparelho, outra aba): o
         pedido morre aqui, e não 5 s depois numa OS aberta nesse meio-tempo.
         No StrictMode o efeito volta na mesma tarefa e marca `retomado` de
         novo antes da microtarefa — e o pedido segue. */
      pedido.retomado = false;
      queueMicrotask(() => { if (!pedido.retomado && abaPedida === pedido) abaPedida = null; });
    };
  }, [aba, estreito, faixa]);
  return useCallback((destino: string) => {
    abaPedida = null;
    const nav = faixa.current;
    if (!estreito || !nav || destino === aba) return;
    const { natural, presa } = pontosDaFaixa(nav);
    /* Grudadas: a página passou do ponto em que as abas encostam no que
       está preso em cima. Antes disso elas estão no lugar natural, à vista,
       e a aba nova já abre logo abaixo delas. */
    if (window.scrollY > natural - presa + 1) abaPedida = { aba: destino, ate: Date.now() + 5000 };
  }, [aba, estreito, faixa]);
}

// ── Escolha em botões ────────────────────────────────────────────────

export interface Opcao {
  valor: string;
  texto: string;
  /* O que vai em cinza ao lado do código, e entra no filtro de quem
     digita (`EscolhaPorCodigo`). Opcional: nem toda opção é um objeto
     com nome — perfil, pessoa e método não têm. */
  detalhe?: string;
  /*
   * Uma opção que existe mas não cabe AGORA — «Descartada» antes do fim
   * do prazo de guarda (0153). Fica visível e desabilitada, e o `title`
   * diz por quê: esconder faria a pessoa procurar a opção em outra tela.
   */
  desabilitada?: boolean;
  porque?: string;
}

/** `Opcao` como o seletor por digitação a espera. */
export const comoCodigo = (o: Opcao): OpcaoDeCodigo =>
  ({ valor: o.valor, codigo: o.texto, detalhe: o.detalhe });

interface EscolhaProps {
  rotulo: string;
  opcoes: Opcao[];
  valor: string | null | undefined;
  aoEscolher(v: string | null): void;
  /** Tocar de novo no que já está escolhido limpa o campo. */
  limpavel?: boolean;
  erro?: string;
  dica?: string;
  classe?: string;
  /**
   * Desabilitada — mostra o que foi escolhido e não deixa trocar.
   *
   * Existe para a tela de interpretação do adensamento, onde quem não
   * tem `ensaio.processar` vê os campos fechados em vez de não vê-los.
   * Esconder faria a pessoa pensar que a etapa não existe; fechar mostra
   * o que falta e de quem é a vez. `disabled` no botão, e não um
   * `onClick` que não faz nada: teclado e leitor de tela precisam saber
   * disso tanto quanto o mouse.
   */
  desabilitado?: boolean;
}

export function Escolha({
  rotulo, opcoes, valor, aoEscolher, limpavel = true, erro, dica, classe,
  desabilitado,
}: EscolhaProps) {
  const id = useId();
  return (
    <div className="campo" role="group" aria-labelledby={id}>
      <span className="rotulo" id={id}>{rotulo}</span>
      <div className={`escolha${classe ? ' ' + classe : ''}`}>
        {opcoes.map((o) => (
          <button
            key={o.valor}
            type="button"
            data-v={o.valor}
            disabled={desabilitado || o.desabilitada}
            title={o.desabilitada ? o.porque : undefined}
            aria-pressed={valor === o.valor}
            onClick={() => aoEscolher(valor === o.valor && limpavel ? null : o.valor)}
          >
            {o.texto}
            {/*
              * O detalhe, quando há — segunda linha, em cinza.
              *
              * «É importante o resultado de Cv aparecer ao lado de cada
              *  estágio para comparar os resultados entre estágios
              *  durante a análise» — Humberto, 01/09/2026.
              *
              * Sem ele, escolher o estágio 3 exigia gravar, voltar ao 1,
              * anotar num papel e voltar — e a coerência entre estágios,
              * que é o critério real de quem interpreta, ficava fora da
              * tela.
              */}
            {o.detalhe && <small className="det-opcao">{o.detalhe}</small>}
          </button>
        ))}
      </div>
      {dica && !erro && <span className="dica">{dica}</span>}
      {erro && <span className="erro" role="alert">{erro}</span>}
    </div>
  );
}

/**
 * Escolher DIGITANDO o número — cápsula, anel, molde, cilindro.
 *
 * ── O pedido, e o gesto real ──────────────────────────────────────────
 *
 * «Para escolha das cápsulas, anéis, moldes nos ensaios gostaria de uma
 *  opção de digitar a numeração de cada um com dropdown para facilitar»
 * — Humberto, 01/09/2026.
 *
 * O gesto do laboratorista é: ele está com a cápsula 118 na mão e olhando
 * para ela. Ele não quer procurar «CAP-0118» numa lista de quarenta — ele
 * quer digitar **118**. A lista suspensa serve para confirmar que achou a
 * certa, não para navegar.
 *
 * Por isso o filtro casa o que se digita em QUALQUER parte do código e do
 * nome: «118» acha `CAP-0118`, «cbr 07» acha `CBR-07 — Cilindro CBR nº 7`,
 * e «ads» acha os seis anéis de adensamento.
 *
 * ── Por que não é um `<select>` com busca do navegador ────────────────
 *
 * Porque a busca do `<select>` casa só o COMEÇO do texto, e o começo é
 * justamente a parte que todas as opções têm igual: digitar «118» num
 * seletor de cápsulas salta para nada, porque nenhuma começa com 1.
 *
 * ── O que ela NÃO faz ─────────────────────────────────────────────────
 *
 * Não aceita texto que não seja uma opção. Sair do campo com «11» meio
 * digitado devolve o que estava escolhido antes — um campo que guarda
 * texto solto parece preenchido e não escolheu nada, que é o pior dos
 * dois estados.
 */
export interface OpcaoDeCodigo {
  valor: string;
  /** O código, em destaque. É o que está gravado no objeto. */
  codigo: string;
  /** O resto — nome, faixa, tara. Entra no filtro e fica em cinza. */
  detalhe?: string;
}

/** Sem acento e em minúsculas: quem digita «capsula» acha «Cápsula». */
const semAcento = (t: string) => t
  .normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();

export function EscolhaPorCodigo({
  rotulo, opcoes, valor, aoEscolher, dica, erro, desabilitado, autoFocus, rotuloOculto,
  placeholder = 'digite o número…', obrigatorio, largo,
}: {
  /** Ocupa a linha inteira — para escolher por nome, não por número (onda 352). */
  largo?: boolean;
  rotulo: string;
  /** O convite do campo vazio — «digite o número…» por padrão (onda 352). */
  placeholder?: string;
  /** Marca o campo como obrigatório para o leitor de tela. */
  obrigatorio?: boolean;
  /** Em tabela (o `<th>` nomeia a coluna): o rótulo só para o leitor de tela. */
  rotuloOculto?: boolean;
  opcoes: OpcaoDeCodigo[];
  valor: string;
  aoEscolher(v: string): void;
  dica?: string;
  erro?: string;
  desabilitado?: boolean;
  autoFocus?: boolean;
}) {
  const id = useId();
  const escolhida = opcoes.find((o) => o.valor === valor);
  const [texto, setTexto] = useState('');
  const [aberto, setAberto] = useState(false);
  const [marcado, setMarcado] = useState(0);
  /* A pessoa andou na lista com as setas (onda 354): o Enter pega a marcada. */
  const [navegou, setNavegou] = useState(false);
  const caixa = useRef<HTMLDivElement>(null);

  /* Quando a escolha vem de fora (carregar a ficha), o campo a mostra. */
  useEffect(() => { setTexto(''); }, [valor]);

  const filtradas = useMemo(() => {
    const termos = semAcento(texto).split(/\s+/).filter(Boolean);
    if (!termos.length) return opcoes;
    return opcoes.filter((o) => {
      const alvo = semAcento(`${o.codigo} ${o.detalhe ?? ''}`);
      return termos.every((t) => alvo.includes(t));
    });
  }, [opcoes, texto]);

  useEffect(() => { setMarcado(0); setNavegou(false); }, [texto]);

  /* Clique fora fecha — e não escolhe. */
  useEffect(() => {
    if (!aberto) return undefined;
    const fora = (e: MouseEvent) => {
      if (caixa.current && !caixa.current.contains(e.target as Node)) {
        setAberto(false);
        setTexto('');
      }
    };
    document.addEventListener('mousedown', fora);
    return () => document.removeEventListener('mousedown', fora);
  }, [aberto]);

  const pegar = (o: OpcaoDeCodigo) => {
    aoEscolher(o.valor);
    setTexto('');
    setAberto(false);
    setNavegou(false);
  };

  const aoTeclar = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      e.preventDefault();
      setAberto(true);
      setNavegou(true);
      setMarcado((m) => {
        const n = filtradas.length;
        if (!n) return 0;
        return e.key === 'ArrowDown' ? (m + 1) % n : (m - 1 + n) % n;
      });
      return;
    }
    if (e.key === 'Enter') {
      /*
       * Enter pega a marcada. Com UMA só na lista, pega aquela sem
       * precisar de seta — é o caso normal de quem digitou o número
       * inteiro, e obrigá-lo a apontar seria devolver o mouse à mão que
       * está segurando a cápsula.
       *
       * Já escolhida, sem nada digitado e sem seta, o Enter não troca a
       * escolha pela primeira da lista: ele passa adiante — na tabela de
       * lançamento, para a célula seguinte (onda 354 · RN-275). O
       * Shift+Enter (voltar uma célula) também não escolhe sem digitar.
       */
      if (aberto && filtradas[marcado] && (texto !== '' || navegou || (!escolhida && !e.shiftKey))) {
        e.preventDefault();
        pegar(filtradas[marcado]);
      } else if (escolhida && texto === '') {
        setAberto(false);
      }
      return;
    }
    if (e.key === 'Escape') {
      setAberto(false);
      setTexto('');
    }
  };

  const listaId = `${id}-lista`;
  /* Sem nada digitado e sem opção nenhuma, a lista não abre: ela diria
     «Nenhum item com «»» por cima da dica que explica o vazio («Nenhuma
     cápsula na estufa.») — achado da onda 349. */
  const listaAberta = aberto && (texto !== '' || opcoes.length > 0);

  return (
    <div className={`campo escolha-codigo${largo ? ' largo' : ''}`} ref={caixa}>
      <label className={rotuloOculto ? 'so-leitor' : 'rotulo'} htmlFor={id}>{rotulo}</label>
      <div className="campo-codigo">
        <input
          id={id}
          type="text"
          role="combobox"
          aria-expanded={listaAberta}
          aria-controls={listaId}
          aria-autocomplete="list"
          autoComplete="off"
          disabled={desabilitado}
          aria-invalid={erro ? true : undefined}
          autoFocus={autoFocus}
          value={texto !== '' ? texto : (escolhida?.codigo ?? '')}
          placeholder={escolhida ? undefined : placeholder}
          aria-required={obrigatorio ? true : undefined}
          onFocus={() => setAberto(true)}
          onKeyDown={aoTeclar}
          onChange={(e) => { setTexto(e.currentTarget.value); setAberto(true); }}
        />
        {escolhida && texto === '' && escolhida.detalhe && (
          <span className="detalhe-escolhido">{escolhida.detalhe}</span>
        )}
      </div>

      {listaAberta && (
        <ul className="lista-codigos" id={listaId} role="listbox">
          {filtradas.length === 0 && (
            <li className="nada" role="presentation">
              Nenhum item com «{texto}».
            </li>
          )}
          {filtradas.slice(0, 60).map((o, i) => (
            <li
              key={o.valor}
              role="option"
              aria-selected={o.valor === valor}
              className={i === marcado ? 'marcado' : undefined}
              onMouseEnter={() => setMarcado(i)}
              onMouseDown={(e) => { e.preventDefault(); pegar(o); }}
            >
              <b className="mono">{o.codigo}</b>
              {o.detalhe && <span className="det">{o.detalhe}</span>}
            </li>
          ))}
          {filtradas.length > 60 && (
            <li className="nada" role="presentation">
              … e mais {filtradas.length - 60}. Digite para filtrar.
            </li>
          )}
        </ul>
      )}

      {dica && !erro && <span className="dica">{dica}</span>}
      {erro && <span className="erro" role="alert">{erro}</span>}
    </div>
  );
}

// ── Avisos ───────────────────────────────────────────────────────────

/**
 * `info` entrou na 0035 e não é enfeite: havia caixas explicando o
 * funcionamento normal do sistema ("a conta nasce sem senha") pintadas de
 * `atencao`, do mesmo âmbar de "isto vai invalidar o link anterior".
 * Quando tudo é alerta, nada é — e a caixa que realmente precisava ser
 * lida some no meio das outras.
 */
export function Aviso({
  tipo = 'erro', titulo, children,
}: {
  tipo?: 'erro' | 'ok' | 'atencao' | 'info';
  titulo?: string;
  children?: ReactNode;
}) {
  return (
    <div className={`aviso ${tipo}`} role={tipo === 'erro' ? 'alert' : 'status'}>
      {titulo && <strong>{titulo}</strong>}
      {children}
    </div>
  );
}

/**
 * O erro da API inteiro, campo a campo.
 *
 * Mostrar só `mensagem` esconderia o que o servidor já sabe: qual campo está
 * errado e por quê. O técnico não pode ter que adivinhar.
 */
export function ErroDaApi({ erro }: { erro: unknown }) {
  if (!erro) return null;
  const api = erro instanceof ErroApi ? erro : null;
  const msg = erro instanceof Error ? erro.message : String(erro);
  /*
   * As recusas item por item (`{ mensagem, recusas: string[] }` — a gravação da importação CPTu,
   * onda 312) entram no MESMO aviso, como os campos. Recusa que não é frase (a do lote de
   * assinaturas manda objetos, e a mensagem já as resume) não aparece aqui.
   */
  const recusas = (api?.recusas ?? []).filter((x): x is string => typeof x === 'string' && x.trim() !== '');
  return (
    <Aviso tipo="erro" titulo={msg}>
      {api && api.campos.length > 0 && (
        <ul>
          {api.campos.map((c, i) => <li key={i}><b>{c.campo}</b>: {c.erro}</li>)}
        </ul>
      )}
      {recusas.length > 0 && (
        <ul>
          {recusas.map((r, i) => <li key={i}>{r}</li>)}
        </ul>
      )}
    </Aviso>
  );
}

/**
 * A resposta da ação, no topo E ao lado do dedo.
 *
 * As fichas de ensaio são longas — dez blocos, cada um com o seu
 * «Gravar» — e a resposta («Gravado.», «Dados inválidos.») morava só no
 * topo da página. No teste de 11/09 (184) o técnico gravava o bloco 7 e
 * o erro aparecia a mil pixels dali, fora da vista: parecia que não
 * gravou. É o mesmo achado da 131 e da 183, em outra tela.
 *
 * Este componente desenha o aviso no lugar de sempre (o topo, onde a
 * ficha inteira o vê) e ECOA uma cópia fixa no rodapé da janela, que
 * aparece quando a resposta muda e some sozinha: o «Gravado.» em seis
 * segundos, o erro quando a pessoa o dispensa ou quando a próxima ação
 * responde. O eco é `aria-hidden`: o leitor de tela já ouve o de cima.
 */
export function RespostaDaAcao({ erro, ok }: { erro: unknown; ok: string | null }) {
  const [eco, setEco] = useState<{ tipo: 'erro' | 'ok'; texto: string } | null>(null);
  const primeira = useRef(true);
  useEffect(() => {
    /* Na primeira renderização não há ação ainda — o erro de carga da
       ficha já aparece no topo e não precisa de eco. */
    if (primeira.current) { primeira.current = false; return; }
    if (erro) {
      setEco({ tipo: 'erro', texto: erro instanceof Error ? erro.message : String(erro) });
      return;
    }
    if (ok) {
      setEco({ tipo: 'ok', texto: ok });
      const t = window.setTimeout(() => setEco(null), 6000);
      return () => window.clearTimeout(t);
    }
    setEco(null);
    return undefined;
  }, [erro, ok]);

  return (
    <>
      {erro ? <ErroDaApi erro={erro} /> : null}
      {ok && !erro && <Aviso tipo="ok">{ok}</Aviso>}
      {eco && (
        <div className={`eco-da-acao ${eco.tipo}`} aria-hidden="true">
          <span>{eco.texto}</span>
          <button type="button" className="ligacao" onClick={() => setEco(null)}>
            fechar
          </button>
        </div>
      )}
    </>
  );
}

export const Carregando = ({ texto = 'Carregando' }: { texto?: string }) => (
  <span className="carregando" role="status" aria-label={texto} />
);

export const Vazio = ({ children }: { children: ReactNode }) => (
  <p className="vazio">{children}</p>
);

// ── Foto protegida ───────────────────────────────────────────────────

/**
 * Uma imagem que exige sessão.
 *
 * `<img src="/api/arquivos/…">` não leva o cabeçalho `Authorization`, então
 * a foto só aparece buscando os bytes e criando um endereço local. O
 * `revokeObjectURL` na saída é o que impede o navegador de segurar doze
 * fotos de celular na memória depois que a tela fechou.
 */
export function FotoProtegida({
  arquivoId, alt, className, semImagem, carregando,
}: {
  arquivoId: string; alt: string; className?: string;
  /**
   * O que mostrar enquanto a foto chega. Sem isto, um `<div>` com «…»; dentro de um botão (a miniatura que abre a
   * foto maior, onda 312) vai um elemento de frase — o `<button>` não leva `<div>`.
   */
  carregando?: ReactNode;
  /**
   * O que mostrar quando a foto não abre — nem pela rede, nem no navegador
   * (a HEIC do iPhone chega inteira e o Chrome não a desenha). Sem isto,
   * «imagem indisponível» (onda 312: a ficha da vertical mostra o nome do
   * arquivo).
   */
  semImagem?: ReactNode;
}) {
  const [src, setSrc] = useState<string | null>(null);
  const [falhou, setFalhou] = useState(false);

  useEffect(() => {
    /* Outro arquivo no mesmo lugar começa do zero: nem a falha nem a imagem da foto anterior ficam. */
    setFalhou(false);
    setSrc(null);
    const abortar = new AbortController();
    let endereco: string | null = null;
    baixarBlob(`/arquivos/${arquivoId}/conteudo`, abortar.signal)
      .then((b) => { endereco = URL.createObjectURL(b); setSrc(endereco); })
      .catch((e) => { if ((e as Error).name !== 'AbortError') setFalhou(true); });
    return () => {
      abortar.abort();
      if (endereco) URL.revokeObjectURL(endereco);
    };
  }, [arquivoId]);

  if (falhou) {
    return semImagem !== undefined ? <>{semImagem}</>
      : <div className="vazio" style={{ padding: '1rem' }}>imagem indisponível</div>;
  }
  if (!src) return carregando !== undefined ? <>{carregando}</> : <div className="vazio" style={{ padding: '1rem' }}>…</div>;
  return <img src={src} alt={alt} className={className} onError={() => setFalhou(true)} />;
}

/**
 * A zona de arquivo — arrastar ou clicar.
 *
 * «Melhore o aspecto de "escolher arquivos", para ficar user-friendly e
 *  mostrar que pode-se arrastar o arquivo até o local ou clicar para
 *  procurar e anexar» — Humberto, 01/09/2026.
 *
 * O `<input type="file">` cru tem dois defeitos que só aparecem em uso:
 * o botão é do SISTEMA OPERACIONAL — muda de nome e de aparência entre
 * Windows, Mac e Android, e não há como dizer nele o que se espera —, e
 * ele não anuncia que aceita arrastar, embora aceite desde sempre. Quem
 * está com o arquivo da prensa aberto ao lado arrasta por instinto, não
 * acontece nada visível, e conclui que não dá.
 *
 * Aqui a área inteira é o alvo: clicar abre o navegador de arquivos,
 * arrastar solta dentro. O `<input>` continua existindo por baixo, e é
 * ele quem faz o trabalho — trocar por um `onClick` que chama `.click()`
 * perderia o teclado e o leitor de tela, que sabem o que é um campo de
 * arquivo e não sabem o que é uma `<div>` clicável.
 *
 * `arraste` NÃO é `dragover` puro: o `dragleave` dispara ao passar por
 * cima de qualquer filho, e a moldura piscaria enquanto o arquivo anda
 * dentro da área. O contador resolve — entra soma, sai subtrai, e só
 * zero apaga a moldura.
 */
export function ZonaDeArquivo({
  rotulo, aceita, tipos, multiplo = false, dica, erro, desabilitado = false,
  arquivos, aoEscolher, textoVazio,
}: {
  rotulo: string;
  /** O `accept` do input — lista técnica, para o seletor do sistema. */
  aceita?: string;
  /**
   * O que dizer à pessoa — «CSV ou XLSX», e não a lista de MIME types.
   * A lista técnica na tela parecia mensagem de erro; ela é para o
   * seletor de arquivos, não para quem lê.
   */
  tipos?: string;
  multiplo?: boolean;
  dica?: string;
  erro?: string;
  desabilitado?: boolean;
  /** Os arquivos já escolhidos, para a zona mostrar o que tem. */
  arquivos?: File[] | File | null;
  aoEscolher(f: File[]): void;
  /** Substitui a frase padrão — «Arraste o arquivo aqui…». */
  textoVazio?: string;
}) {
  const id = useId();
  const [dentro, setDentro] = useState(0);
  const escolhidos = Array.isArray(arquivos)
    ? arquivos : arquivos ? [arquivos] : [];

  const receber = (lista: FileList | null) => {
    if (desabilitado || !lista || lista.length === 0) return;
    aoEscolher(multiplo ? [...lista] : [lista[0]]);
  };

  /* Com a vírgula decimal da casa («1,2 MB»), pelo formatador de números (agent-browser da 312). */
  const tamanho = (b: number) => (b < 1024 ? `${b} B`
    : b < 1024 * 1024 ? `${numero(b / 1024, 0)} kB`
      : `${numero(b / 1024 / 1024, 1)} MB`);

  return (
    <div className="campo">
      <span className="rotulo">{rotulo}</span>
      <label
        htmlFor={id}
        className={`zona-arquivo${dentro > 0 ? ' sobre' : ''}`
          + `${escolhidos.length ? ' cheia' : ''}`
          + `${desabilitado ? ' inerte' : ''}`}
        onDragEnter={(e) => { e.preventDefault(); setDentro((n) => n + 1); }}
        onDragOver={(e) => { e.preventDefault(); }}
        onDragLeave={() => setDentro((n) => Math.max(0, n - 1))}
        onDrop={(e) => {
          e.preventDefault();
          setDentro(0);
          receber(e.dataTransfer?.files ?? null);
        }}
      >
        <input
          id={id}
          type="file"
          accept={aceita}
          multiple={multiplo}
          disabled={desabilitado}
          aria-describedby={erro ? `${id}-e` : dica ? `${id}-d` : undefined}
          onChange={(e) => { receber(e.currentTarget.files); }}
        />
        {escolhidos.length === 0 ? (
          <>
            <span className="zona-icone" aria-hidden="true">⬇</span>
            <span className="zona-frase">
              {textoVazio ?? 'Arraste o arquivo aqui'}
              <b> ou clique para procurar</b>
            </span>
            {tipos && <span className="zona-tipos">{tipos}</span>}
          </>
        ) : (
          <>
            <span className="zona-icone" aria-hidden="true">▣</span>
            <span className="zona-frase">
              {escolhidos.map((f) => (
                <span key={f.name} className="zona-nome">
                  {f.name} <i>{tamanho(f.size)}</i>
                </span>
              ))}
            </span>
            <span className="zona-tipos">
              {multiplo ? 'Arraste outros ou clique para trocar'
                : 'Arraste outro ou clique para trocar'}
            </span>
          </>
        )}
      </label>
      {dica && !erro && <span className="dica" id={`${id}-d`}>{dica}</span>}
      {erro && <span className="erro" id={`${id}-e`} role="alert">{erro}</span>}
    </div>
  );
}

/**
 * Segurar para confirmar.
 *
 * «Para essas opções deve abrir um pop-up de confirmação ou botão que
 *  deve ser segurado durante X segundos (no caso de dispositivos
 *  móveis)» — Humberto, 01/09/2026.
 *
 * ── Por que segurar, e não «tem certeza?» ────────────────────────────
 *
 * Uma caixa de «tem certeza?» é lida uma vez e depois nunca mais: a mão
 * aprende o caminho até o segundo botão e o aviso vira um obstáculo a
 * ser vencido, não uma pergunta a ser respondida. Segurar não tem esse
 * desgaste — o gesto DURA, e durante a duração a pessoa está lendo o
 * que vai acontecer, porque não há mais nada a fazer com a mão.
 *
 * E resolve o caso do tablet na bancada, que é onde o dedo escorrega:
 * um toque acidental não segura um segundo e meio.
 *
 * O gesto é cancelável até o fim — soltar antes zera a barra. Quem
 * começou a apertar e mudou de ideia não é punido pela hesitação.
 *
 * Teclado tem o mesmo tratamento por Enter ou Espaço: `repeat` do
 * autorrepeat é ignorado, senão a tecla presa dispararia sozinha.
 *
 * ── O botão diz que é de segurar (183) ───────────────────────────────
 *
 * No teste da 182 pela tela, «Concluir e conceder» recebeu um clique e
 * não fez nada — e a pessoa concluiu que o certificado «não sai». O
 * gesto é bom; o que faltava era o botão avisar que o gesto é outro:
 * um «segure» miúdo ao lado do texto o tempo todo, e, quando solta
 * cedo, a explicação por dois segundos no lugar do texto.
 */
export function SegurarParaConfirmar({
  texto, aoConfirmar, desabilitado = false, tom = 'perigo', segundos = 1.5,
}: {
  texto: string;
  aoConfirmar(): void;
  desabilitado?: boolean;
  tom?: 'perigo' | 'normal';
  segundos?: number;
}) {
  const [progresso, setProgresso] = useState(0);
  const [soltouCedo, setSoltouCedo] = useState(false);
  const quadro = useRef<number | null>(null);
  const comeco = useRef(0);
  const feito = useRef(false);
  const avisoCedo = useRef<number | null>(null);

  const parar = () => {
    const estavaSegurando = quadro.current !== null;
    if (quadro.current !== null) cancelAnimationFrame(quadro.current);
    quadro.current = null;
    setProgresso(0);
    if (estavaSegurando && !feito.current) {
      setSoltouCedo(true);
      if (avisoCedo.current !== null) window.clearTimeout(avisoCedo.current);
      avisoCedo.current = window.setTimeout(() => setSoltouCedo(false), 2200);
    }
  };

  useEffect(() => () => {
    if (quadro.current !== null) cancelAnimationFrame(quadro.current);
    if (avisoCedo.current !== null) window.clearTimeout(avisoCedo.current);
  }, []);

  const passo = () => {
    const p = Math.min(1, (performance.now() - comeco.current) / (segundos * 1000));
    setProgresso(p);
    if (p >= 1) {
      feito.current = true;
      parar();
      aoConfirmar();
      return;
    }
    quadro.current = requestAnimationFrame(passo);
  };

  const comecar = () => {
    if (desabilitado || quadro.current !== null) return;
    feito.current = false;
    setSoltouCedo(false);
    comeco.current = performance.now();
    quadro.current = requestAnimationFrame(passo);
  };

  return (
    <button
      type="button"
      className={`botao segurar${tom === 'perigo' ? ' perigo' : ''}`}
      disabled={desabilitado}
      aria-describedby="segurar-dica"
      onPointerDown={(e) => { e.preventDefault(); comecar(); }}
      onPointerUp={parar}
      onPointerLeave={parar}
      onPointerCancel={parar}
      onKeyDown={(e) => {
        if ((e.key === 'Enter' || e.key === ' ') && !e.repeat) {
          e.preventDefault(); comecar();
        }
      }}
      onKeyUp={parar}
      onBlur={parar}
    >
      <span className="segurar-barra" style={{ width: `${progresso * 100}%` }} />
      <span className="segurar-texto">
        {progresso > 0 ? 'Segure…'
          : soltouCedo ? 'Segure até a barra encher'
          : /segur/i.test(texto) ? texto
          : <>{texto} <small className="segurar-dica">segure</small></>}
      </span>
    </button>
  );
}

// ── Diálogo ──────────────────────────────────────────────────────────

/*
 * SP-347d (Humberto, onda 347, opção A): o `Dialogo` mora em
 * `cptu/visual/Dialogo.tsx`, a pasta que a bancada e o Portal já têm byte a
 * byte (300.90), para que a janela «Escala» dos gráficos do CPTu use a mesma
 * peça nos dois sites. Daqui ele só é reexportado: as telas continuam
 * importando de `./ui`, sem mudar nada.
 */
export { Dialogo } from './cptu/visual/Dialogo';

// ── Regra de negócio na tela ─────────────────────────────────────────

/**
 * O código da regra que governa aquele ponto da tela — `RN-151`, `RN-024`.
 *
 * Não é enfeite de protótipo, é requisito (`05-ux-e-prototipo` §2.3): quando
 * o sistema recusa alguma coisa, o técnico precisa entender POR QUÊ sem
 * abrir manual — e numa auditoria, quem opera precisa conseguir apontar a
 * regra na tela em vez de dizer "o sistema não deixa".
 *
 * `title` leva a frase inteira, para quem quiser o texto sem decorar o
 * catálogo de regras.
 */
export function Rn({ codigo, diz }: { codigo: string; diz?: string }) {
  return <abbr className="rn" title={diz ?? `Regra de negócio ${codigo}`}>{codigo}</abbr>;
}

// ── Selo de estado ───────────────────────────────────────────────────

export type Tom = 'ok' | 'atencao' | 'critico' | 'andamento' | 'neutro';

/**
 * Estado com SÍMBOLO, texto e cor — nessa ordem de importância.
 *
 * A cor é a terceira: quem tem deuteranopia, quem imprime o laudo em
 * preto e branco e quem está no sol do canteiro leem o símbolo e a
 * palavra. Requisito do briefing §18 e do protótipo §2.2.
 */
/**
 * O selo, com explicação opcional ao passar o mouse.
 *
 * `titulo` existe para o caso da lista do parque: o selo diz «calibração
 * vencida» e o `title` diz a frase inteira, que não cabe na coluna. Não
 * é a única saída — no celular não há mouse —, e por isso a linha inteira
 * leva à ficha, onde a explicação está escrita por extenso. O `title` é
 * atalho para quem tem mouse, nunca o único caminho.
 */
export function Selo({ tom, titulo, children }: {
  tom: Tom; titulo?: string; children: ReactNode;
}) {
  return <span className={`selo ${tom}`} title={titulo}>{children}</span>;
}

// ── O selo do ensaio, um só (267) ────────────────────────────────────

/**
 * A situação do ensaio no mesmo selo em toda tela (267).
 *
 * Até a 266 cada tela tinha o seu mapa de cor: a Fila pintava «aguardando
 * verificação» de amarelo, o Mapa de azul, a OS de azul e a grade da
 * programação só mostrava a sigla. O mesmo ensaio mudava de cor ao trocar
 * de tela. Aqui fica a escada inteira — símbolo, palavra e tom — e quem
 * mostra a situação de um ensaio usa este selo.
 *
 * As chaves são as do `status_ensaio` (0001) — todas, e só elas (o
 * `231.10` confere contra o banco).
 */
export const SITUACAO_DO_ENSAIO: Record<string, { simbolo: string; texto: string; tom: Tom }> = {
  programado:              { simbolo: '◷', texto: 'Programado',             tom: 'neutro' },
  em_execucao:             { simbolo: '▶', texto: 'Em execução',            tom: 'andamento' },
  pausado:                 { simbolo: '‖', texto: 'Pausado',                tom: 'atencao' },
  dados_completos:         { simbolo: '▶', texto: 'Dados completos',        tom: 'andamento' },
  em_processamento:        { simbolo: '▶', texto: 'Em processamento',       tom: 'andamento' },
  aguardando_verificacao:  { simbolo: '?', texto: 'Aguardando verificação', tom: 'atencao' },
  devolvido_para_correcao: { simbolo: '!', texto: 'Devolvido para correção', tom: 'critico' },
  aprovado:                { simbolo: '✓', texto: 'Aprovado',               tom: 'ok' },
  cancelado:               { simbolo: '×', texto: 'Cancelado',              tom: 'neutro' },
  substituido:             { simbolo: '×', texto: 'Substituído',            tom: 'neutro' },
};

/** O símbolo da situação, para quem só tem espaço para um caractere. */
export const simboloDoEnsaio = (status?: string | null) =>
  SITUACAO_DO_ENSAIO[status ?? '']?.simbolo ?? '•';

/** A palavra da situação. */
export const textoDoEnsaio = (status?: string | null) =>
  SITUACAO_DO_ENSAIO[status ?? '']?.texto
    ?? (status ? status.charAt(0).toUpperCase() + status.slice(1).replace(/_/g, ' ') : '—');

export function SeloDoEnsaio({ status, titulo }: {
  status: string | null | undefined; titulo?: string;
}) {
  const s = SITUACAO_DO_ENSAIO[status ?? ''];
  return (
    <span className={`selo ${s?.tom ?? 'neutro'} selo-ensaio`}
      data-status={status ?? ''} title={titulo}>
      <span className="simbolo" aria-hidden="true">{s?.simbolo ?? '•'}</span>
      {textoDoEnsaio(status)}
    </span>
  );
}

/**
 * O selo de um degrau de escada de situação (`situacoes.ts`, P-UI-41): o
 * mesmo molde do `SeloDoEnsaio` — símbolo da escada, palavra e tom —, para a
 * campanha CPTu, a vertical, a conferência da importação e o cone na data
 * (onda 312). `texto` troca só a palavra (a conta: «2 avisos»); o tom e o
 * símbolo continuam os da escada.
 */
export function SeloDaEscada({ degrau, texto, titulo }: {
  degrau: { simbolo: string; texto: string; tom: Tom }; texto?: string; titulo?: string;
}) {
  return (
    <span className={`selo ${degrau.tom} selo-ensaio`} title={titulo}>
      <span className="simbolo" aria-hidden="true">{degrau.simbolo}</span>
      {texto ?? degrau.texto}
    </span>
  );
}

// ── Abrir o que a API só entrega autenticado ─────────────────────────

/**
 * Abre um documento de `/api` numa aba nova.
 *
 * Existe porque `<a href="/api/…">` NÃO funciona neste sistema. Um `<a>` é
 * uma navegação do navegador, não uma chamada do `api()`: sai sem
 * `Authorization`, e o guarda devolve `Sessão ausente.` (401) em vez do
 * documento. É o mesmo motivo do `FotoProtegida` acima — o token mora no
 * `localStorage`, e só quem monta a requisição em JavaScript o anexa.
 * Nenhuma tela deve voltar a apontar `href` para `/api`.
 *
 * Três detalhes que parecem manha e não são:
 *
 * 1. A aba é aberta ANTES do primeiro `await`, ainda dentro do gesto do
 *    usuário. Aberta depois, o navegador a trata como pop-up e bloqueia —
 *    o técnico clica e não acontece nada, sem erro nenhum na tela.
 * 2. Se o bloqueador pegar mesmo assim, o documento desce como arquivo.
 *    Sumir calado não é opção.
 * 3. O erro é escrito DENTRO da aba. Ela já está na frente do técnico; uma
 *    aba em branco enquanto a mensagem aparece atrás é como se perde a
 *    explicação do que houve.
 *
 * O `revokeObjectURL` demora um minuto de propósito: soltar os bytes antes
 * de a aba terminar de renderizar dá página em branco e nenhuma pista. O
 * laudo é autocontido — logo e fotos vão em base64, ver `pdf.service.ts` —
 * então o endereço local basta, não falta nada para buscar depois.
 */
export async function abrirProtegido(caminho: string, nomeQueda: string): Promise<void> {
  const janela = window.open('', '_blank');
  if (janela) {
    janela.document.write(
      '<!doctype html><meta charset="utf-8"><title>Gerando o documento…</title>'
      + '<body style="font:16px/1.5 system-ui,sans-serif;padding:2rem;color:#555">'
      + 'Gerando o documento…');
    janela.document.close();
  }
  try {
    const blob = await baixarBlob(caminho);
    const endereco = URL.createObjectURL(blob);
    /* Só abre na aba o que a aba pode mostrar sem rodar nada que não seja da
       casa (`baixarBlob`): o resto desce como arquivo, com o nome, e a aba
       que já tinha aberto fecha. */
    const naAba = abreNumaAba(blob.type);
    if (!naAba && janela && !janela.closed) janela.close();
    if (naAba && janela && !janela.closed) {
      janela.location.replace(endereco);
    } else {
      const a = document.createElement('a');
      a.href = endereco;
      a.download = nomeQueda;
      document.body.appendChild(a);
      a.click();
      a.remove();
    }
    setTimeout(() => URL.revokeObjectURL(endereco), 60_000);
  } catch (e) {
    if (janela && !janela.closed) {
      const texto = (e instanceof Error ? e.message : 'Erro inesperado.')
        .replace(/[&<>]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;' })[c]!);
      janela.document.open();
      janela.document.write(
        '<!doctype html><meta charset="utf-8"><title>Não deu</title>'
        + '<body style="font:16px/1.5 system-ui,sans-serif;padding:2rem;color:#912">'
        + `<strong>Não consegui abrir o documento.</strong><p>${texto}`);
      janela.document.close();
    }
    throw e;
  }
}

/**
 * As ações do laudo — a prévia AQUI, a prévia em aba, e o PDF.
 *
 * ─── Por que a prévia embutida ────────────────────────────────────────
 *
 * Conferir a formatação do laudo custava abrir uma aba, olhar, voltar,
 * corrigir um número, abrir de novo. Quatro gestos para cada vírgula fora
 * do lugar, e a tela do ensaio perdida de vista no meio do caminho — que
 * é justamente onde se corrige.
 *
 * A prévia abre embaixo, na mesma página, e fecha do mesmo jeito. É o
 * item 6 do documento 75, tirado da tela da Suporte Infra: «não precisa
 * finalizar para conferir o PDF».
 *
 * ─── Por que num `<iframe>`, e por que `sandbox` ──────────────────────
 *
 * O laudo traz o CSS dele — margens de folha A4, cabeçalho repetido,
 * tipografia de documento. Despejar isso no DOM da tela faria as duas
 * folhas de estilo brigarem, e quem perderia seria a que estivesse
 * depois. O `<iframe>` dá ao laudo a página inteira que ele supõe ter.
 *
 * `sandbox` sem permissão nenhuma porque nada ali precisa rodar: é
 * documento, não aplicação. E porque o HTML carrega dados de cliente —
 * um `<script>` que aparecesse ali um dia não teria de onde falar com a
 * sessão de quem está olhando.
 *
 * ─── O que ela NÃO é ──────────────────────────────────────────────────
 *
 * Não é emissão. Continua preliminar, com a marca d'água que o servidor
 * põe, e nada sobe ao acervo. A `PD-85` é que vai abrir a emissão de
 * verdade — e mesmo depois dela esta prévia continua, porque conferir
 * antes de emitir é o passo que ela existe para servir.
 */
export function AcoesDoLaudo({ ensaioId, caminho, nome = 'laudo' }: {
  ensaioId: string;
  /**
   * O caminho do documento, quando NÃO é o laudo do ensaio.
   *
   * Nasceu com o diário de campo (onda 119): as três ações — conferir
   * aqui, ver em outra aba, gerar o PDF — são as mesmas para qualquer
   * documento da casa, e uma segunda cópia delas divergiria da primeira
   * como as cópias sempre divergem. O padrão continua sendo o laudo,
   * para que o literal `/ensaios/:id/laudo` continue escrito aqui — é
   * por ele que o teste 26 acha o caminho na tela.
   */
  caminho?: string;
  /** O começo do nome do arquivo baixado. */
  nome?: string;
}) {
  const [ocupado, setOcupado] = useState<'laudo' | 'pdf' | 'previa' | null>(null);
  const [erro, setErro] = useState<unknown>(null);
  const [previa, setPrevia] = useState<string | null>(null);

  function abrir(qual: 'laudo' | 'pdf') {
    if (ocupado) return;
    setErro(null);
    setOcupado(qual);
    const base = caminho ?? `/ensaios/${ensaioId}/laudo`;
    abrirProtegido(
      `${base}${qual === 'pdf' ? '.pdf' : ''}`,
      `${nome}-${ensaioId.slice(0, 8)}.${qual === 'pdf' ? 'pdf' : 'html'}`,
    ).catch(setErro).finally(() => setOcupado(null));
  }

  async function alternarPrevia() {
    if (ocupado) return;
    if (previa) { setPrevia(null); return; }
    setErro(null);
    setOcupado('previa');
    try {
      setPrevia(await api<string>(caminho ?? `/ensaios/${ensaioId}/laudo`,
        { cru: true }));
    } catch (e) {
      setErro(e);
    } finally { setOcupado(null); }
  }

  return (
    <>
      <div className="acoes">
        <button type="button" className="botao" disabled={ocupado !== null}
          onClick={() => void alternarPrevia()}>
          {ocupado === 'previa' ? 'Montando…'
            : previa ? 'Fechar a prévia' : 'Conferir aqui'}
        </button>
        <button type="button" className="botao" disabled={ocupado !== null}
          onClick={() => abrir('laudo')}>
          {ocupado === 'laudo' ? 'Abrindo…' : 'Ver em outra aba'}
        </button>
        <button type="button" className="botao principal" disabled={ocupado !== null}
          onClick={() => abrir('pdf')}>
          {ocupado === 'pdf' ? 'Gerando o PDF…' : 'Gerar o PDF'}
        </button>
      </div>
      <ErroDaApi erro={erro} />

      {previa !== null && (
        <div className="previa-laudo">
          <p className="sub">
            Prévia preliminar, do jeito que o PDF sai. Nada foi emitido nem
            guardado no acervo — corrigir acima e conferir de novo aqui é o
            caminho, e não gerar um PDF a cada tentativa.
          </p>
          {/*
            * `isolado`: esta prévia não imprime, então o `sandbox` fica
            * fechado. O preço é a altura não ser medida — e é um preço
            * barato perto de abrir a sessão de quem confere ao HTML.
            */}
          <FolhaA4 titulo="Prévia do laudo" corpo={previa} isolado />
        </div>
      )}
    </>
  );
}

/**
 * O mesmo, com cara de link, para quando o documento é um detalhe da linha
 * e não a ação principal da tela — a fila, o certificado do equipamento, a
 * folha de plaquetas.
 *
 * É um `<button>` de propósito, e não um `<a>` sem `href`: o leitor de tela
 * anuncia "botão", que é o que isto é, e o teclado aciona com Espaço além
 * do Enter. O erro aparece na aba que já abriu.
 */
export function LinkProtegido({
  caminho, nomeQueda, className, style, children,
}: {
  caminho: string; nomeQueda: string; className?: string;
  style?: CSSProperties; children: ReactNode;
}) {
  const [ocupado, setOcupado] = useState(false);
  return (
    <button
      type="button"
      className={className ?? 'como-link'}
      style={style}
      disabled={ocupado}
      onClick={() => {
        if (ocupado) return;
        setOcupado(true);
        abrirProtegido(caminho, nomeQueda)
          .catch(() => { /* a aba já mostra o motivo */ })
          .finally(() => setOcupado(false));
      }}
    >
      {children}
    </button>
  );
}

/**
 * O documento dentro de um quadro isolado.
 *
 * Não abro em aba nova porque a rota exige o cabeçalho `Authorization`, e
 * uma aba nova não o leva.
 *
 * Mora aqui, e não na tela de recebimento onde nasceu, desde que a lista
 * de amostras passou a imprimir etiquetas em lote (onda 31): duas telas
 * mostrando documento do SGQ com duas cópias do mesmo visor é como as
 * duas passam a divergir na primeira correção (`D-29`). O HTML já vem com as imagens embutidas em base64,
 * então `srcDoc` basta — e `sandbox` sem `allow-same-origin` garante que o
 * documento não alcança a sessão de quem o está vendo.
 */
/**
 * A folha A4, para o documento ser visto do tamanho em que ele sai.
 *
 * ── O que estava errado ──────────────────────────────────────────────
 *
 * «O pré-visualizar de qualquer documento está pegando a largura total
 *  da tela. Corrija para ficar na largura final que seria uma página A4»
 * — Testesolo, 02/09/2026.
 *
 * As três prévias do sistema — a janela cheia, a prévia do laudo e o
 * diálogo da OS — abriam o documento num quadro de largura total. Num
 * monitor de 27" a tabela de trinta amostras se esparramava por 2000 px,
 * e o que a pessoa via não tinha relação com o que ia sair na
 * impressora: colunas largas demais, quebras de linha em outros lugares,
 * cabeçalho desproporcional. Conferir um documento numa largura que não
 * é a dele é conferir outro documento.
 *
 * ── Por que 186 mm, e não 210 ────────────────────────────────────────
 *
 * O gerador declara `@page { size: A4; margin: 14mm 12mm 16mm 12mm }`.
 * A folha tem 210 mm, mas a área ONDE O CONTEÚDO CABE tem 210 − 12 − 12
 * = 186 mm. No navegador o `@page` não vale para a tela: o corpo ocupa
 * o que o quadro der.
 *
 * Então a folha branca aqui tem os 210 mm e a mesma margem por dentro,
 * e o quadro fica com os 186 mm que sobram. É a mesma conta que o PDF
 * faz, feita do lado de fora — e por isso a prévia quebra a linha onde o
 * papel vai quebrar.
 *
 * ── A altura ─────────────────────────────────────────────────────────
 *
 * Quando o quadro é da mesma origem, a altura vem do conteúdo e a folha
 * cresce: o documento rola como papel, e não dentro de uma janelinha.
 * Quando ele é isolado (`sandbox` sem `allow-same-origin`, que é o caso
 * das prévias que não precisam imprimir), medir é proibido — e aí vale
 * a altura pedida, com rolagem por dentro. Enfraquecer o isolamento
 * para ganhar uma medida de altura seria trocar segurança por conforto.
 */
export function FolhaA4({
  titulo, corpo, isolado, altura, aoQuadro,
}: {
  titulo: string;
  corpo: string;
  /** `true` mantém o `sandbox` fechado — e aí a altura não é medida. */
  isolado?: boolean;
  /** Altura quando não dá para medir. */
  altura?: string;
  /** Para quem precisa do quadro depois — imprimir, por exemplo. */
  aoQuadro?(q: HTMLIFrameElement | null): void;
}) {
  const [quadro, setQuadro] = useState<HTMLIFrameElement | null>(null);
  const [alto, setAlto] = useState<number | null>(null);

  const guardar = useCallback((q: HTMLIFrameElement | null) => {
    setQuadro(q);
    aoQuadro?.(q);
  }, [aoQuadro]);

  /*
   * A altura do conteúdo, medida depois que ele carrega.
   *
   * `scrollHeight` do `documentElement` e não do `body`: o corpo pode
   * ter margem colapsada, e aí a medida sai curta e o fim do documento
   * fica cortado — que é pior do que não medir.
   */
  useEffect(() => {
    if (isolado || !quadro) return;
    let vivo = true;
    const medir = () => {
      if (!vivo) return;
      try {
        const d = quadro.contentDocument;
        const h = d?.documentElement?.scrollHeight ?? 0;
        if (h > 0) setAlto(h);
      } catch { /* origem diferente: fica a altura pedida */ }
    };
    quadro.addEventListener('load', medir);
    /* O `srcDoc` pode já ter carregado antes deste efeito rodar. */
    medir();
    /* E de novo depois das imagens em base64 assentarem. */
    const t = setTimeout(medir, 300);
    return () => {
      vivo = false;
      clearTimeout(t);
      quadro.removeEventListener('load', medir);
    };
  }, [quadro, corpo, isolado]);

  return (
    <div className="folha-visor">
      <div className="folha">
        <iframe
          ref={guardar}
          title={titulo}
          srcDoc={corpo}
          sandbox={isolado ? '' : 'allow-same-origin allow-modals'}
          style={{
            width: '100%', border: 0, display: 'block', background: '#fff',
            height: alto ? `${alto}px` : (altura ?? '70vh'),
          }}
        />
      </div>
    </div>
  );
}

export function JanelaDocumento({
  titulo, corpo, aoFechar,
}: { titulo: string; corpo: string; aoFechar(): void }) {
  const [quadro, setQuadro] = useState<HTMLIFrameElement | null>(null);
  return (
    <div
      role="dialog" aria-label={titulo} aria-modal="true"
      style={{
        position: 'fixed', inset: 0, zIndex: 50,
        background: 'var(--fundo)', display: 'flex', flexDirection: 'column',
      }}
    >
      <div className="topo">
        <h1 className="cresce">{titulo}</h1>
        <button type="button" onClick={() => quadro?.contentWindow?.print()}>Imprimir</button>
        <button type="button" onClick={aoFechar}>Fechar</button>
      </div>
      <FolhaA4 titulo={titulo} corpo={corpo} aoQuadro={setQuadro} />
    </div>
  );
}

/**
 * A barra de avanço, com o número ao lado.
 *
 * Existe aqui, e não em cada tela, porque ela nasceu copiada em quatro
 * lugares — lista de OS, ficha da OS, lista de amostras e ficha da
 * amostra — e as quatro cópias tinham o mesmo defeito: barra sozinha,
 * fina, cinza, com o percentual em outra linha. Consertar quatro vezes
 * é a receita para consertar três.
 *
 * Duas regras que a marcação carrega e o CSS não conseguiria:
 *
 *   · **1 % tem que aparecer.** Numa barra de 11 rem, 1 % dá 1,7 px —
 *     some. `max(pct%, .7rem)` garante um toco visível para qualquer
 *     avanço maior que zero. Zero continua zero: «começou pouco» e «não
 *     começou» são situações diferentes e a barra tem que dizer qual é.
 *
 *   · **Cheia se diz cheia.** `data-cheia` sai daqui porque o CSS não
 *     enxerga o número; é ele que pinta a barra de verde em 100 %.
 */
export function BarraDeAvanco({
  pct, rotulo,
}: {
  pct: number;
  /** O que esta barra mede — vai para o leitor de tela. */
  rotulo: string;
}) {
  const limitado = Math.max(0, Math.min(100, Math.round(pct)));
  const cheia = limitado >= 100;

  return (
    <div className="avanco-linha">
      <div className="avanco-barra" role="progressbar"
        data-cheia={cheia ? 'sim' : 'nao'}
        aria-valuenow={limitado} aria-valuemin={0} aria-valuemax={100}
        aria-label={rotulo}>
        <span style={{
          width: limitado === 0 ? '0' : `max(${limitado}%, .7rem)`,
        }} />
      </div>
      <span className={cheia ? 'avanco-pct completa' : 'avanco-pct'}>
        {limitado}%
      </span>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════
// Colunas que ordenam
// ═══════════════════════════════════════════════════════════════════════

/**
 * Um cabeçalho de coluna que ordena.
 *
 * «Colocar uma seta para ordenar em ordem crescente ou decrescente ao
 *  lado de cada palavra da 1ª linha» — Testesolo, 02/09/2026.
 * «Sempre coloque setas para ordenar em ordem cronológica/alfabética em
 *  cada coluna da planilha» — Testesolo, 02/09/2026.
 *
 * O «sempre» é a razão de isto ter saído da tela de amostras e vindo
 * para cá. Enquanto morava numa tela só, a segunda tabela que precisasse
 * ordenar ia copiar o componente — e duas cópias do mesmo comportamento
 * é como as duas divergem (`D-29`): uma ganha `aria-sort`, a outra não;
 * uma inverte no segundo clique, a outra recomeça.
 *
 * Três decisões que valem escrever:
 *
 *   · A seta aparece em TODAS as colunas, fraca na inativa e acesa na
 *     ativa. Eu tinha escondido a inativa para não encher a linha de
 *     ruído; a Testesolo pediu o contrário, e tem razão sobre o que
 *     importa mais — coluna que não mostra que é clicável não é
 *     descoberta.
 *   · O clique ALTERNA o sentido na mesma coluna e começa na direção
 *     natural da coluna numa coluna nova: crescente no texto, e
 *     decrescente na data, porque o que se procura numa lista de datas
 *     é quase sempre o mais recente.
 *   · `aria-sort` acompanha, para quem usa leitor de tela saber a ordem
 *     sem depender do desenho da seta.
 */
export function ColunaOrdenavel({
  campo, texto, opcional, classe, ordem, sentido, aoTrocar,
}: {
  campo: string;
  texto: string;
  opcional?: boolean;
  classe?: string;
  ordem: string;
  sentido: 'asc' | 'desc';
  aoTrocar(campo: string): void;
}) {
  const ativa = ordem === campo;
  return (
    <th
      scope="col"
      className={['ordenavel', opcional ? 'col-opcional' : '', classe ?? '',
        ativa ? 'ativa' : ''].filter(Boolean).join(' ')}
      aria-sort={ativa
        ? (sentido === 'asc' ? 'ascending' : 'descending')
        : 'none'}
    >
      <button
        type="button"
        onClick={() => aoTrocar(campo)}
        title={ativa
          ? `Ordenado por ${texto}, ${sentido === 'asc' ? 'crescente' : 'decrescente'}`
            + ' — clique para inverter'
          : `Ordenar por ${texto}`}
      >
        {texto}
        <span className="seta" aria-hidden="true">
          {ativa ? (sentido === 'asc' ? '▲' : '▼') : '↕'}
        </span>
      </button>
    </th>
  );
}

/**
 * O estado de ordenação de uma tabela.
 *
 * `descendentes` são as colunas cuja direção natural é do maior para o
 * menor — datas e contagens de dias. Clicar numa delas pela primeira vez
 * traz o mais recente ao topo, e não o de 2019; clicar de novo inverte.
 * Sem essa lista, «ordenar por data de aprovação» abriria a lista pelo
 * ensaio mais velho do arquivo, que não é o que ninguém procura.
 */
export function useOrdenacao(inicial: string, opcoes?: {
  descendentes?: string[];
  sentidoInicial?: 'asc' | 'desc';
}) {
  const desc = opcoes?.descendentes ?? [];
  const [ordem, setOrdem] = useState(inicial);
  const [sentido, setSentido] = useState<'asc' | 'desc'>(
    opcoes?.sentidoInicial ?? (desc.includes(inicial) ? 'desc' : 'asc'));

  const trocar = useCallback((campo: string) => {
    setOrdem((atual) => {
      if (atual === campo) {
        setSentido((s) => (s === 'asc' ? 'desc' : 'asc'));
        return atual;
      }
      setSentido(desc.includes(campo) ? 'desc' : 'asc');
      return campo;
    });
    // `desc` é literal de chamada e muda de identidade a cada render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /*
   * Trocar a coluna SEM passar por um cabeçalho — o seletor «ordenar
   * por» da lista de amostras escreve aqui. Sem esta porta, as duas
   * peças teriam estados separados e diriam coisas diferentes sobre a
   * mesma lista.
   */
  const definir = useCallback((campo: string) => {
    setOrdem(campo);
    setSentido(desc.includes(campo) ? 'desc' : 'asc');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return { ordem, sentido, trocar, definir };
}

/**
 * Ordena uma lista na memória por uma chave de coluna.
 *
 * Existe para as tabelas que já vêm inteiras da API — a de OS esperando
 * assinatura tem dezenas de linhas, não milhares, e mandá-la de volta ao
 * servidor a cada clique de seta seria trocar uma comparação por uma
 * viagem de rede. Onde a lista é grande e cortada por `LIMIT`, a
 * ordenação continua sendo do servidor: ordenar só a página visível
 * mentiria sobre o que está fora dela.
 *
 * `nulos` vão sempre para o fim, nos dois sentidos: «não tem» não é nem
 * o maior nem o menor, e vê-los no topo esconde a primeira linha útil.
 */
export function ordenarPor<T>(
  linhas: T[],
  chave: (l: T) => string | number | null | undefined,
  sentido: 'asc' | 'desc',
): T[] {
  const sinal = sentido === 'asc' ? 1 : -1;
  return [...linhas].sort((a, b) => {
    const x = chave(a);
    const y = chave(b);
    const vazioX = x == null || x === '';
    const vazioY = y == null || y === '';
    if (vazioX && vazioY) return 0;
    if (vazioX) return 1;
    if (vazioY) return -1;
    if (typeof x === 'number' && typeof y === 'number') return (x - y) * sinal;
    return String(x).localeCompare(String(y), 'pt-BR',
      { numeric: true, sensitivity: 'base' }) * sinal;
  });
}

// ═══════════════════════════════════════════════════════════════════════
// A ação que a permissão não deixa
// ═══════════════════════════════════════════════════════════════════════

/**
 * O botão que continua na tela, desabilitado e com o motivo escrito.
 *
 * ── O relato ─────────────────────────────────────────────────────────
 *
 * «Em ordem de serviço não estou encontrando como cria uma Nova OS» —
 * Testesolo, 02/09/2026. E o sistema estava certo: ele estava vestindo a
 * persona «Responsável técnico (demonstração)», que tem `os.ver` e não
 * tem `os.criar`. O botão sumiu porque a permissão faltava.
 *
 * ── Por que sumir é pior do que recusar ──────────────────────────────
 *
 * Esconder parece elegante — «não mostro o que você não pode» —, mas
 * quem olha não distingue «você não pode» de «isto não existe». O dono
 * do sistema procurou o próprio botão de abrir OS e não achou; um
 * técnico, no lugar dele, abriria um chamado dizendo que o sistema não
 * cria OS.
 *
 * O que fica escondido continua escondido no BANCO: a rota recusa do
 * mesmo jeito, e este componente não afrouxa nada. O que muda é só a
 * tela dizer o nome da permissão que falta em vez de calar.
 *
 * ── O que ele NÃO faz ────────────────────────────────────────────────
 *
 * Não vale para tudo. Uma tela cheia de botões cinzentos que ninguém
 * pode apertar é ruído — este é para a AÇÃO PRINCIPAL da tela, a que
 * responde «para que serve esta página».
 */
export function AcaoBloqueada(props: {
  /** O rótulo que o botão teria se a permissão existisse. */
  texto: string;
  /** O verbo, para a frase: «não abre OS», «não aprova ensaio». */
  oQueFaz: string;
  /** O nome legível da permissão que falta. */
  permissao: string;
  /** O nome da persona vestida, quando há uma. */
  persona?: string | null;
} | {
  texto: string;
  /**
   * O motivo que não é de permissão — o registro que já não muda (a campanha
   * CPTu em verificação, onda 312): a frase vai como está.
   */
  motivo: string;
}) {
  const { texto } = props;
  const motivo = 'motivo' in props ? props.motivo
    : props.persona
      ? `Você está atuando como ${props.persona}, que ${props.oQueFaz}. `
        + 'Volte a ser você na tarja do topo.'
      : `Seu perfil ${props.oQueFaz} — falta a permissão «${props.permissao}». `
        + 'Peça a quem administra os usuários.';
  return (
    <span className="acao-bloqueada" style={{ flex: '0 0 auto' }}>
      <button type="button" className="botao" disabled title={motivo}>
        {texto}
      </button>
      <small className="det">{motivo}</small>
    </span>
  );
}
